/*
 * NAGOYA — motion design (sans dépendance, chargé en defer)
 * Attributs : data-nagoya-reveal, data-nagoya-stagger, data-nagoya-draw,
 * data-nagoya-parallax, data-nagoya-progress, data-nagoya-steps.
 * Aussi : photo partagée carte → fiche (pageswap / pagereveal), pastille du panier, retour d'ajout au panier,
 * grilles insérées après coup, hauteur de l'en-tête collant (--nagoya-header-h sur :root), classe is-visible des
 * bandeaux de collaboration (leurs animations attendent l'écran), éditeur de thème.
 * Mouvement réduit : les attributs gardent leur sens, mais tout est affiché d'emblée (is-revealed, is-drawn posées
 * aussitôt, sans observer l'écran ni cascade), y compris si le visiteur active ce réglage en cours de visite.
 * Détails : docs/motion-design.md
 */
(() => {
  const root = document.documentElement;
  if (root.classList.contains('nagoya-motion') || !('IntersectionObserver' in window)) return;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const phone = matchMedia('(max-width: 749px)');
  const STAGGER = 90;
  const WAVE = 4; // charte : quatre éléments au plus par vague
  const SHAPES = 'path, line, polyline, circle, ellipse';
  const CARDS = '.product-card, .article-card, .collection-card';

  // Cibles automatiques du thème : [sélecteur, mode, délai de base en ms]
  const AUTO = [
    ['.nagoya-recit__opening, .nagoya-chapitre__text, .nagoya-recit__quote, .nagoya-pierres, .nagoya-faq', ''],
    ['.nagoya-recit__hero, .nagoya-chapitre__media', 'clip'],
    ['.nagoya-chapitre__detail', '', 450],
    // Cartes des grilles et piliers (classe « nagoya-pilier » des blocs Groupe) : en cascade, par vagues de quatre
    [CARDS + ', .nagoya-pilier', ''],
  ];

  const inView = (el) => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
  };

  // L'élément lui-même s'il correspond, puis ses descendants
  const within = (scope, sel) => [...(scope.matches?.(sel) ? [scope] : []), ...scope.querySelectorAll(sel)];

  /* Apparitions : les éléments d'un même lot entrent en cascade */
  const revealIO = new IntersectionObserver(
    (entries) => {
      let k = 0;
      entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
        .forEach((e) => {
          const el = e.target;
          const i = el.dataset.nagoyaIndex != null ? +el.dataset.nagoyaIndex : k++;
          const delay = (+el.dataset.nagoyaDelay || 0) + (i % WAVE) * STAGGER;
          if (delay) el.style.setProperty('--nagoya-delay', delay + 'ms');
          el.classList.add('is-revealed');
          revealIO.unobserve(el);
        });
    },
    { rootMargin: '0px 0px -8% 0px' }
  );

  /* Tracés */
  const drawIO = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-drawn');
        drawIO.unobserve(e.target);
      }),
    { rootMargin: '0px 0px -10% 0px' }
  );

  /* Mouvement réduit : tout est affiché d'emblée, sans attendre l'écran (ni fondu, ni masque, ni tracé progressif) */
  const showAll = (scope) => {
    within(scope, '[data-nagoya-reveal]:not(.is-revealed)').forEach((el) => {
      revealIO.unobserve(el);
      el.classList.add('is-revealed');
    });
    within(scope, '[data-nagoya-draw]:not(.is-drawn)').forEach((svg) => {
      drawIO.unobserve(svg);
      svg.classList.add('is-drawn');
    });
  };

  /* Bandeaux de collaboration : is-visible à l'entrée dans l'écran ; la section y suspend ses animations jusque-là */
  const VISIBLE = '.nagoya-collab-hero';
  const visibleIO = new IntersectionObserver((entries) =>
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-visible');
      visibleIO.unobserve(e.target);
    })
  );

  /* Séquences : l'étape lue au centre de l'écran découvre les calques jusqu'à son rang */
  const setStep = (group, n) => {
    group.dataset.nagoyaActive = n;
    group.querySelectorAll('[data-nagoya-step]').forEach((s) => s.classList.toggle('is-current', +s.dataset.nagoyaStep === n));
    group.querySelectorAll('[data-nagoya-layer]').forEach((l) => l.classList.toggle('is-shown', +l.dataset.nagoyaLayer <= n));
  };
  const stepsIO = new IntersectionObserver(
    (entries) =>
      entries.forEach((e) => e.isIntersecting && setStep(e.target.closest('[data-nagoya-steps]'), +e.target.dataset.nagoyaStep)),
    { rootMargin: '-55% 0px -35% 0px' }
  );

  /* Parallaxe et lignes de progression : un seul rAF par image */
  const parallax = new Set();
  const progress = new Set();
  const tracked = new WeakSet(); // lignes déjà suivies : un second passage (éditeur, grille insérée) ne les double pas
  const parallaxIO = new IntersectionObserver((entries) =>
    entries.forEach((e) => (e.isIntersecting ? parallax.add(e.target) : parallax.delete(e.target)))
  );

  let ticking = false;
  const schedule = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  };

  function frame() {
    ticking = false;
    const vh = innerHeight;
    // Charte : pas de parallaxe sur téléphone, 40 px au plus sur ordinateur
    const still = reduce.matches || phone.matches;
    parallax.forEach((el) => {
      // On mesure le parent : l'élément lui-même est déplacé
      const r = el.parentElement.getBoundingClientRect();
      const f = parseFloat(el.dataset.nagoyaParallax);
      const y = still ? 0 : Math.max(-40, Math.min(40, (r.top + r.height / 2 - vh / 2) * -(isNaN(f) ? 0.08 : f)));
      el.style.setProperty('--nagoya-parallax-y', y.toFixed(1) + 'px');
    });
    progress.forEach(({ line, track }) => {
      let p;
      const max = track.scrollWidth - track.clientWidth;
      if (max > 2) {
        p = track.scrollLeft / max;
      } else if (reduce.matches) {
        p = 1;
      } else {
        const r = track.getBoundingClientRect();
        p = (vh * 0.6 - r.top) / r.height;
      }
      line.style.setProperty('--nagoya-progress', Math.min(1, Math.max(0, p)).toFixed(3));
    });
  }

  /* Hauteur de l'en-tête collant, exposée sur :root (--nagoya-header-h, 0 s'il ne colle pas) : le hero « côte à côte »
     plein écran et les scènes collantes la retirent. --header-height n'existe que sur <header-component>. */
  const headerRO = 'ResizeObserver' in window ? new ResizeObserver(() => setHeaderH()) : null;
  let header = null;
  const setHeaderH = () => {
    const h = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
    root.style.setProperty('--nagoya-header-h', h + 'px');
  };
  const watchHeader = () => {
    const el = document.querySelector('.section-header');
    if (el === header) return;
    if (header) headerRO?.unobserve(header);
    header = el;
    if (header) headerRO?.observe(header);
    setHeaderH();
  };

  function scan(scope, fresh) {
    const all = (sel) => within(scope, sel);

    AUTO.forEach(([sel, mode, delay]) =>
      all(sel).forEach((el) => {
        if (el.hasAttribute('data-nagoya-reveal') || el.closest('.splide, [data-nagoya-static]')) return;
        el.setAttribute('data-nagoya-reveal', mode);
        if (delay) el.dataset.nagoyaDelay = delay;
      })
    );

    all('[data-nagoya-stagger]').forEach((group) =>
      [...group.children].forEach((child, i) => {
        if (!child.hasAttribute('data-nagoya-reveal')) child.setAttribute('data-nagoya-reveal', '');
        child.dataset.nagoyaIndex = i;
      })
    );

    // Mouvement réduit : tout est affiché d'emblée, sans observateur ni cascade (voir showAll) ; les deux passages
    // suivants n'ont alors plus rien à faire
    if (reduce.matches) showAll(scope);

    // Ce qui est déjà à l'écran reste tel quel : aucun retard sur le premier affichage.
    // Contenu inséré après coup (fresh) : il n'a jamais été vu, il peut donc entrer en cascade.
    let k = 0;
    all('[data-nagoya-reveal]').forEach((el) => {
      if (el.classList.contains('is-revealed')) return;
      if (!inView(el)) return revealIO.observe(el);
      if (!fresh) return el.classList.add('is-revealed');
      el.style.setProperty('--nagoya-delay', (k++ % WAVE) * STAGGER + 'ms');
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-revealed')));
    });

    // Premier passage, avant la pose de .nagoya-motion : un dessin à l'écran déjà tracé (sans la classe nagoya-js, ou
    // parce que le filet de sécurité CSS a joué avant l'arrivée du script) reste tel quel, sans s'effacer pour se redessiner.
    const first = !root.classList.contains('nagoya-motion');
    all('[data-nagoya-draw]:not(.is-drawn)').forEach((svg) => {
      const shapes = svg.querySelectorAll(SHAPES);
      if (first && shapes[0] && inView(svg) && parseFloat(getComputedStyle(shapes[0]).strokeDashoffset) < 0.5) {
        svg.classList.add('is-drawn');
        return;
      }
      const step = parseFloat(svg.dataset.nagoyaDraw) || 0.06;
      shapes.forEach((p, i) => p.style.setProperty('--nagoya-delay', (i * step).toFixed(2) + 's'));
      // Entièrement à l'écran (la Gogane du hero, posée au ras du bas) : il se dessine aussitôt, sans attendre la marge
      // basse de l'observateur. Deux images d'attente : le tracé part du dessin masqué déjà rendu.
      const r = svg.getBoundingClientRect();
      if (r.height > 0 && r.top >= 0 && r.bottom <= innerHeight) {
        requestAnimationFrame(() => requestAnimationFrame(() => svg.classList.add('is-drawn')));
        return;
      }
      drawIO.observe(svg);
    });

    all(VISIBLE + ':not(.is-visible)').forEach((el) => (inView(el) ? el.classList.add('is-visible') : visibleIO.observe(el)));

    all('[data-nagoya-steps]').forEach((group) => {
      setStep(group, +group.dataset.nagoyaActive || 0);
      group.querySelectorAll('[data-nagoya-step]').forEach((s) => stepsIO.observe(s));
    });

    all('[data-nagoya-parallax]').forEach((el) => parallaxIO.observe(el));

    all('[data-nagoya-progress]').forEach((line) => {
      if (tracked.has(line)) return;
      tracked.add(line);
      const zone = line.closest('[data-nagoya-progress-scope]') || line.parentElement;
      const track = zone.querySelector('[data-nagoya-progress-track]') || zone;
      progress.add({ line, track });
      track.addEventListener('scroll', schedule, { passive: true });
    });

    schedule();
  }

  watchHeader();
  scan(document);
  root.classList.add('nagoya-motion');

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  // Mouvement réduit activé en cours de visite : ce qui attendait d'apparaître ou de se dessiner s'affiche aussitôt
  reduce.addEventListener?.('change', () => {
    if (reduce.matches) showAll(document);
    schedule();
  });

  /* Éditeur de thème */

  // Lignes de progression qui ne sont plus suivies ; un passage ultérieur de scan() pourra les reprendre
  const untrack = (keep) =>
    progress.forEach((item) => {
      if (keep(item.line)) return;
      progress.delete(item);
      tracked.delete(item.line);
    });

  // Section ajoutée ou recalculée : le script s'y relance comme au chargement
  document.addEventListener('shopify:section:load', (e) => {
    untrack((line) => line.isConnected);
    watchHeader();
    scan(e.target);
  });

  // Section supprimée ou sur le point d'être recalculée (encore dans la page) : plus rien ne la suit ni ne la retient
  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target;
    untrack((line) => !section.contains(line));
    section.querySelectorAll(`[data-nagoya-reveal], [data-nagoya-draw], [data-nagoya-step], [data-nagoya-parallax], ${VISIBLE}`).forEach((el) => {
      revealIO.unobserve(el);
      drawIO.unobserve(el);
      stepsIO.unobserve(el);
      parallaxIO.unobserve(el);
      visibleIO.unobserve(el);
      parallax.delete(el);
    });
  });

  // Bloc choisi dans la barre latérale : l'éditeur ne fait défiler la page que verticalement. Dans une piste horizontale
  // (planches, étapes sur mobile), on amène le bloc à l'écran et on l'affiche. Pas de défilement animé (mouvement réduit).
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    if (!block.closest?.('.nagoya-planches__piste, .nagoya-etapes__list')) return;
    block.scrollIntoView({ block: 'nearest', inline: 'start' });
    block.querySelectorAll('[data-nagoya-reveal]:not(.is-revealed)').forEach((el) => {
      el.classList.add('is-revealed');
      revealIO.unobserve(el);
    });
  });

  // Grilles remplacées après coup (filtres, tri, recommandations) : les nouvelles cartes entrent en cascade
  new MutationObserver((records) =>
    records.forEach((r) =>
      r.addedNodes.forEach((n) => {
        if (n.nodeType === 1 && (n.matches(CARDS) || n.querySelector(CARDS))) scan(n, true);
      })
    )
  ).observe(document.body, { childList: true, subtree: true });

  /* Carte produit → fiche : la photo de la carte glisse jusqu'à sa place (View Transitions entre documents).
     Sur la fiche, la première photo de la galerie porte le même nom (CSS) ; au retour, la photo rejoint sa carte. */
  let lastCard = null; // la carte réellement cliquée l'emporte si le même produit figure deux fois sur la page
  addEventListener('click', (e) => (lastCard = e.target.closest?.('product-card[data-product-url]') || lastCard), true);
  const pieceOf = (url) => {
    const path = url && new URL(url).pathname;
    const same = (c) => c?.isConnected && new URL(c.dataset.productUrl, location.href).pathname === path;
    const card = path && (same(lastCard) ? lastCard : [...document.querySelectorAll('product-card[data-product-url]')].find(same));
    return card?.querySelector('.product-media img');
  };
  const clearPiece = () => {
    root.classList.remove('nagoya-vt-carte');
    document.querySelectorAll('[data-nagoya-piece]').forEach((img) => {
      img.style.viewTransitionName = '';
      delete img.dataset.nagoyaPiece;
    });
  };
  const namePiece = (img) => {
    if (!img || reduce.matches) return;
    root.classList.add('nagoya-vt-carte'); // la photo de galerie de cette page cède son nom : jamais deux éléments du même nom
    img.style.viewTransitionName = 'nagoya-piece';
    img.dataset.nagoyaPiece = '';
  };
  addEventListener('pageswap', (e) => {
    clearPiece();
    if (e.viewTransition) namePiece(pieceOf(e.activation?.entry?.url));
  });
  addEventListener('pagereveal', (e) => {
    clearPiece();
    if (!e.viewTransition) return;
    // Retour (précédent / suivant) seulement : la photo de la fiche quittée rejoint sa carte
    const act = window.navigation?.activation;
    if (act?.navigationType === 'traverse') namePiece(pieceOf(act.from?.url));
    e.viewTransition.finished.then(clearPiece, clearPiece);
  });

  /* Panier. product-form.js transmet aussi les réponses d'erreur de Shopify (422 : rupture, quantité maximale) comme
     des ajouts réussis. Avec le tiroir, il demande les sections du panier, que Shopify ne renvoie que si l'ajout a
     réussi : leur absence trahit un échec. Sans tiroir, rien ne permet de trancher. */
  const failedAdd = (data) => !!data.didError || (data.source === 'product-form' && !data.sections && !!document.querySelector('cart-drawer'));

  // La pastille du nombre d'articles se pose quand une pièce est ajoutée (en mouvement réduit, rien ne bouge : seul le
  // nombre change, la feuille de style neutralise la classe)
  const poseTimers = new WeakMap();
  let cartCount = parseInt(document.querySelector('cart-icon [data-ref="cart-count"]')?.textContent, 10) || 0;
  document.addEventListener('cart:updated', (e) => {
    const data = e.detail?.data || {};
    if (failedAdd(data) || !Number.isFinite(data.itemCount)) return;
    if (data.itemCount > cartCount) {
      document.querySelectorAll('cart-icon .header__icon-count-bubble').forEach((b) => {
        b.classList.remove('nagoya-pose');
        requestAnimationFrame(() => b.classList.add('nagoya-pose'));
        // Retirée après l'anneau (0,9 s), le plus long des deux gestes
        clearTimeout(poseTimers.get(b));
        poseTimers.set(b, setTimeout(() => b.classList.remove('nagoya-pose'), 1100));
      });
    }
    cartCount = data.itemCount;
  });

  /* Ajout au panier : un fil court sous le libellé pendant l'envoi. Il ne se fond en « ajouté » que sur un signal de
     réussite (panier mis à jour par le formulaire, ou notification de succès) : jamais au bout d'un délai, ni après
     une erreur. product-form.js reste intact. */
  document.addEventListener(
    'submit',
    (e) => {
      const button = e.target.closest?.('product-form')?.querySelector('[data-ref="add-to-cart-button"]');
      if (!button) return;
      let over = false;
      const done = (sent) => {
        if (over) return;
        over = true;
        observer.disconnect();
        clearTimeout(timer);
        document.removeEventListener('cart:updated', onCart);
        document.removeEventListener('toast:open', onToast);
        button.classList.remove('nagoya-atc--sending');
        if (!sent) return;
        button.classList.add('nagoya-atc--done');
        setTimeout(() => button.classList.remove('nagoya-atc--done'), 1300);
      };
      const onCart = (ev) => {
        const data = ev.detail?.data || {};
        if (data.source === 'product-form') done(!failedAdd(data));
      };
      const onToast = (ev) => {
        const type = ev.detail?.type;
        if (type === 'success') done(true);
        if (type !== 'error') return;
        done(false);
        // Après une erreur réseau, product-form.js laisse le bouton en attente : il redevient utilisable
        button.classList.remove('is-loading');
      };
      // .is-loading retiré sans signal de réussite : échec. On laisse finir la tâche en cours, une notification pouvant suivre.
      const observer = new MutationObserver(() => {
        if (!button.classList.contains('is-loading')) setTimeout(() => done(false));
      });
      const timer = setTimeout(() => done(false), 10000);
      document.addEventListener('cart:updated', onCart);
      document.addEventListener('toast:open', onToast);
      button.classList.remove('nagoya-atc--done');
      button.classList.add('nagoya-atc--sending');
      // product-form.js pose .is-loading juste après cet écouteur (phase de capture)
      requestAnimationFrame(() => {
        if (over) return;
        if (!button.classList.contains('is-loading')) return done(false);
        observer.observe(button, { attributes: true, attributeFilter: ['class'] });
      });
    },
    true
  );
})();
